import { Component, OnInit, OnDestroy, AfterViewInit } from '@angular/core';
import { Router } from "@angular/router";
import { Observable, Subscription, BehaviorSubject } from "rxjs";
import { tap } from "rxjs/operators";
//import { GridOptions, ColDef, AllModules, CellEvent } from "@ag-grid-enterprise/all-modules";
import { GridOptions, ColDef, GridApi } from "ag-grid-community";
import { LoaderService } from "../../../common/services/loader.service";
import { CanDeactivateComponent } from "../../../common/classes/can-deactivate.component";
import { DynamicDialogService } from "../../../core/services/dynamic-dialog.service";
import { Dialog, DialogButton } from "../../../core/models/dialog.model";
import { PermitService } from "../../../common/services/permit.service";
import { GridFormatterService } from "../../../common/services/grid-formatter.service";
import { CountyInspector, InspectorListSearchCriteria } from "../../../common/models/permit.model";
import { AuthService } from "../../../services/auth.service";
import { SubHeader } from '../../../core/models/sub-header.model';
import { SubHeaderService } from '../../../core/services/sub-header.service';

@Component({
    selector: 'app-manage-inspectors',
    templateUrl: './manage-inspectors.component.html',
    styleUrls: ['./manage-inspectors.component.scss'],
    standalone: false
})
export class ManageInspectorsComponent extends CanDeactivateComponent implements OnInit, OnDestroy, AfterViewInit {

    confirmDeactivate: Dialog = new Dialog("You have unsubmitted changes.  Are you sure you wish to leave the page?", [new DialogButton("Cancel", false, () => { return false; }), new DialogButton("OK", true, () => { this.deactivateConfirmed(); })]);
    confirmReplace: Dialog = new Dialog("", [new DialogButton("Cancel", false, () => { this.cancelReplace(); }), new DialogButton("OK", true, () => { this.replaceAll(); })]);
    confirmSaved: Dialog = new Dialog("Inspector list has been updated.", [new DialogButton("OK", true, () => { this.exit(); } )]);

    confirmReplaceText = "This will replace all instances of [original] with [replaced].  Are you sure?  This action is irreversible aside from exiting the page without saving.";

    
    private dataSubject: BehaviorSubject<CountyInspector[]> = new BehaviorSubject<CountyInspector[]>(null);
    data: Observable<CountyInspector[]> = this.dataSubject.asObservable();

    

    gridOptions: GridOptions;
	gridApi?: GridApi;

    originalValue: string = "";
    replacementValue: string = "";

    subscription: Subscription = new Subscription();

    countyColumn: ColDef = {
        headerClass: "wrapped",
        headerName: "County",
        headerTooltip: "County",
        field: "countyName",
        tooltipField: "countyName",
        editable: false,
        minWidth: 125,
        maxWidth: 125,
        filter: "agTextColumnFilter",
        filterParams: this.formatter.defaultTextFilterParams,
        pinned: "left"
    } as ColDef;

    inspectorColumn: ColDef = {
        headerClass: "wrapped",
        headerName: "Inspector Name",
        headerTooltip: "Inspector Name",
        field: "inspectorName",
        tooltipField: "inspectorName",
        editable: true,
        filter: "agTextColumnFilter",
        filterParams: this.formatter.defaultTextFilterParams,
        minWidth: 200
    } as ColDef;

    inspectorPhoneColumn: ColDef = {
        headerClass: "wrapped",
        headerName: "Inspector Phone #",
        headerTooltip: "Inspector Phone #",
        field: "inspectorPhone",
        tooltipField: "inspectorPhone",
        editable: true,
        filter: "agTextColumnFilter",
        filterParams: this.formatter.defaultTextFilterParams,
        minWidth: 150
    } as ColDef;

    backupInspectorColumn: ColDef = {
        headerClass: "wrapped",
        headerName: "Backup Inspector Name",
        headerTooltip: "Backup Inspector Name",
        field: "backupInspectorName",
        tooltipField: "backupInspectorName",
        editable: true,
        filter: "agTextColumnFilter",
        filterParams: this.formatter.defaultTextFilterParams,
        minWidth: 200
    } as ColDef;

    backupInspectorPhoneColumn: ColDef = {
        headerClass: "wrapped",
        headerName: "Backup Inspector Phone #",
        headerTooltip: "Backup Inspector Phone #",
        field: "backupInspectorPhone",
        tooltipField: "backupInspectorPhone",
        editable: true,
        filter: "agTextColumnFilter",
        filterParams: this.formatter.defaultTextFilterParams,
        minWidth: 225,
        maxWidth: 250
    } as ColDef;

    supervisorColumn: ColDef = {
        headerClass: "wrapped",
        headerName: "Supervisor Name",
        headerTooltip: "Supervisor Name",
        field: "supervisorName",
        tooltipField: "supervisorName",
        editable: true,
        filter: "agTextColumnFilter",
        filterParams: this.formatter.defaultTextFilterParams,
        minWidth: 200
    } as ColDef;

    supervisorPhoneColumn: ColDef = {
        headerClass: "wrapped",
        headerName: "Supervisor Phone #",
        headerTooltip: "Supervisor Phone #",
        field: "supervisorPhone",
        tooltipField: "supervisorPhone",
        editable: true,
        filter: "agTextColumnFilter",
        filterParams: this.formatter.defaultTextFilterParams,
        minWidth: 150
    } as ColDef;

    regionalPhoneColumn: ColDef = {
        headerClass: "wrapped",
        headerName: "Regional Phone #",
        headerTooltip: "Regional Phone #",
        field: "regionalPhone",
        tooltipField: "regionalPhone",
        editable: true,
        filter: "agTextColumnFilter",
        filterParams: this.formatter.defaultTextFilterParams,
        minWidth: 150
    } as ColDef;

    columns: ColDef[] = [
        this.countyColumn, 
        this.inspectorColumn,
        this.inspectorPhoneColumn,
        this.backupInspectorColumn,
        this.backupInspectorPhoneColumn,
        this.supervisorColumn,
        this.supervisorPhoneColumn,
        this.regionalPhoneColumn
    ] as ColDef[];

    subHeader: SubHeader = new SubHeader({ title: "Manage Inspector List" });

    constructor(dialog: DynamicDialogService, private permitService: PermitService, private formatter: GridFormatterService, private router: Router, private loader: LoaderService,
                auth: AuthService, private subHeaderService: SubHeaderService) {
		super(dialog, auth);
    }

    ngOnInit() {
        this.subHeaderService.updateSubHeader(this.subHeader);
        this.confirmDialog = this.confirmDeactivate;
        this.gridOptions = this.formatter.BuildDefaultGridOptionsWithColumns(this.columns, false, false, false);
        this.gridOptions.headerHeight = 50;
        this.gridOptions.singleClickEdit = true;
        this.gridOptions.stopEditingWhenCellsLoseFocus = true;
		this.gridOptions.pagination = false;
        this.gridOptions.rowHeight = 40;
		this.gridOptions.onGridReady = (params) => { this.gridApi = params.api; };
        this.gridOptions.onCellValueChanged = ($event) => this.onGridSelecting($event);
        const data$ = this.permitService.searchInspectorList({} as InspectorListSearchCriteria)
            .pipe(
                tap((inspectors) => {
                    this.dataSubject.next(inspectors);
                })
            ).subscribe(
                {
                    next: () => { },
                    error: (error) => { this.handleError(error); }
                }
            );
        this.subscription.add(data$);
    }

    ngOnDestroy() {
        super.ngOnDestroy();
        if (this.subscription != null) this.subscription.unsubscribe();
    }

    ngAfterViewInit() {
		if (this.gridApi) this.gridApi.sizeColumnsToFit();
    }

    cancel(): void {
        this.router.navigate(["/home"]);
    }

    cancelReplace(): void {
        this.confirmReplace.message = this.confirmReplaceText;
    }

    confirmReplaceAll(): void {
        this.confirmReplace.message = this.confirmReplaceText.replace("[original]", this.originalValue).replace("[replaced]", this.replacementValue);
        this.confirmReplace.toggleDialog(true);
        this.dialogService.updateDialog(this.confirmReplace);
    }

    deactivateConfirmed(): void {
        this.allowDeactivate.next(true);
    }

    exit(): void {
        this.allowDeactivate.next(true);
        this.router.navigate(["/home"]);
    }

    handleError(error: any): void {
        console.error(error);
        this.loader.toggleLoader(false);
        this.dialogService.showError();
    }

    onGridSelecting(evt: any): void {
        this.allowDeactivate.next(false);
    }

    replaceAll(): void {
        let inspectors = this.dataSubject.value;
        if (inspectors != null) {
            const disallowedProperties: string[] = ["countyCode", "countyName", "rowKey"];
            inspectors.forEach(i => {
                for (var property in i) {
                    if (disallowedProperties.findIndex(p => p == property) === -1) {
                        if (i[property] == this.originalValue) i[property] = this.replacementValue;
                    }
                }
            });
            this.dataSubject.next(inspectors);
            if (this.gridApi) this.gridApi.setGridOption("rowData", inspectors);
            this.allowDeactivate.next(false);
        }
        this.originalValue = "";
        this.replacementValue = "";
    }

    submitChanges(): void {
        if (this.allowDeactivate.value == false) {
            const inspectors: CountyInspector[] = this.dataSubject.value;
            this.loader.toggleLoader(true);
            const saves$ = this.permitService.updateInspectors(inspectors)
                .pipe(
                    tap((updated: CountyInspector[]) => {
                        this.dataSubject.next(updated);
                    })
                ).subscribe(
                    (data: CountyInspector[]) => {
                        this.loader.toggleLoader(false);
                        this.confirmSaved.toggleDialog(true);
                        this.dialogService.updateDialog(this.confirmSaved);
                    },
                    (error) => { this.handleError(error); }
            );
            this.subscription.add(saves$);
        }
    }
}
